import {
  Card,
  EmptyState,
  Feed,
  Header,
  IconNotification,
  IconTimer,
  Space,
  TextButton,
  WithBadge,
} from '@procurify/ui'
import { pascalCase } from 'change-case'

import { useAppRoutes, useFeatureFlag, useProcurifyIntl } from '@webapp/hooks'
import { ContractListPageStatuses } from '@webapp/pages/ContractListPage'
import { getFullName } from '@webapp/utils/user'
import messages from './messages'
import { InboxListNotificationMetaStyled } from './styles/InboxListNotificationMetaStyled'
import { InboxListStyled } from './styles/InboxListStyled'
import { FormatContentType } from '../../core/hoc/FormatContentType/FormatContentType'
import { type Inbox } from '../../models'
import { InboxNotification } from '../InboxNotification'
import {
  type InboxNotificationType,
  InboxNotificationTypes,
} from '../InboxNotification/types'
import { UserAvatar } from '../UserAvatar'

// tslint:disable-next-line: no-var-requires
const procurifyCompactLogo = require('!svg-url-loader!../../assets/img/procurify-logo-compact.svg')

export interface IInboxListProps {
  notifications: Inbox[]
  onMarkAllAsRead: () => void
  onRead: (notification: Inbox) => void
}

export const InboxList = ({
  notifications,
  onMarkAllAsRead,
  onRead,
}: IInboxListProps) => {
  const intl = useProcurifyIntl()
  const { url } = useAppRoutes()
  const PUR_3723_SEQUENTIAL_ORDER_NUMBER = useFeatureFlag(
    'PUR_3723_SEQUENTIAL_ORDER_NUMBER'
  )

  const renderMeta = (notification: Inbox) => {
    const { object_pk, object_number } = notification.message
    const displayNumber = PUR_3723_SEQUENTIAL_ORDER_NUMBER
      ? (object_number ?? object_pk)
      : object_pk

    return (
      <InboxListNotificationMetaStyled>
        <FormatContentType id={notification.message.content_type}>
          {(contentType) => (
            <a
              href={notification.message.permalink}
              onClick={() => onRead(notification)}
            >
              {pascalCase(contentType.model)} #{displayNumber}
            </a>
          )}
        </FormatContentType>
      </InboxListNotificationMetaStyled>
    )
  }

  const getNotificationType = (notification: Inbox): InboxNotificationType => {
    switch (true) {
      case notification.metadata?.is_delegation:
        return InboxNotificationTypes.DELEGATION
      case notification.metadata?.is_contract_batch:
        return InboxNotificationTypes.CONTRACT_BATCH
      case !!notification.metadata?.punchout:
        return InboxNotificationTypes.PUNCHOUT
      case notification.message?.is_ping:
        return InboxNotificationTypes.PING
      case notification.metadata?.is_linked_bank_account:
        return InboxNotificationTypes.LINKED_BANK_ACCOUNT
      default:
        return InboxNotificationTypes.DEFAULT
    }
  }

  const isPing = (notification: Inbox) => {
    return getNotificationType(notification) === InboxNotificationTypes.PING
  }

  const isDelegation = (notification: Inbox) => {
    return (
      getNotificationType(notification) === InboxNotificationTypes.DELEGATION
    )
  }

  const renderBullet = (notification: Inbox) => {
    return notification.message.userprofile ? (
      <WithBadge
        placement='bottom-right'
        content={
          isPing(notification) ? (
            <IconNotification
              size={13}
              aria-label={intl.formatMessage(messages.reminderIconLabel)}
            />
          ) : (
            <IconTimer
              size={13}
              aria-label={intl.formatMessage(messages.delegationIconLabel)}
            />
          )
        }
        show={isPing(notification) || isDelegation(notification)}
      >
        <UserAvatar
          size='medium'
          name={getFullName(notification.message.userprofile)}
          userId={notification.message.userprofile.id}
          url={notification.message.userprofile.profile_image}
        />
      </WithBadge>
    ) : (
      <div className='logo-bullet'>
        <img
          src={procurifyCompactLogo}
          alt={intl.formatMessage(messages.procurifyLogoAltText)}
        />{' '}
      </div>
    )
  }

  const renderFeedContent = (notification: Inbox) => {
    const notificationType = getNotificationType(notification)
    switch (notificationType) {
      case InboxNotificationTypes.PING:
        return (
          <InboxNotification
            action={intl.formatMessage(messages.reminderActionMessage)}
            onRead={() => onRead(notification)}
            notification={notification}
            metas={renderMeta(notification)}
            isReminder
          />
        )
      case InboxNotificationTypes.DELEGATION:
        return (
          <InboxNotification
            onRead={() => onRead(notification)}
            notification={notification}
            hideLink
            action={intl.formatMessage(messages.delegateActionText, {
              name: getFullName(
                notification.metadata?.delegation?.approval_delegatee
              ),
            })}
          />
        )
      case InboxNotificationTypes.CONTRACT_BATCH:
        return (
          <InboxNotification
            onRead={() => onRead(notification)}
            notification={notification}
            link={url('ContractListPage', {
              status: ContractListPageStatuses.DRAFT,
            })}
          />
        )
      case InboxNotificationTypes.PUNCHOUT:
        return (
          <InboxNotification
            onRead={() => onRead(notification)}
            notification={notification}
            link={url('OrderDetailsPage', {
              uuid: notification.metadata.punchout.order_uuid,
            })}
          />
        )
      case InboxNotificationTypes.LINKED_BANK_ACCOUNT:
        return (
          <InboxNotification
            onRead={() => onRead(notification)}
            notification={notification}
          />
        )
      default:
        return (
          <InboxNotification
            onRead={() => onRead(notification)}
            notification={notification}
            metas={renderMeta(notification)}
          />
        )
    }
  }

  return (
    <InboxListStyled>
      <Card>
        <Card.Header>
          <Space direction='horizontal' justify='space-between'>
            <Header as='h5'>
              {intl.formatMessage(messages.inboxListTitle)}
            </Header>
          </Space>
          <TextButton onClick={onMarkAllAsRead}>
            {intl.formatMessage(messages.markAllAsRead)}
          </TextButton>
        </Card.Header>
        <Card.Content maxHeight={400}>
          {!notifications.length && (
            <EmptyState
              header={intl.formatMessage(messages.emptyStateHeader)}
            />
          )}

          {!!notifications.length && (
            <Feed>
              {notifications.map((notification) => (
                <Feed.Row
                  key={notification.id}
                  bullet={renderBullet(notification)}
                >
                  <Feed.Content>{renderFeedContent(notification)}</Feed.Content>
                </Feed.Row>
              ))}
            </Feed>
          )}
        </Card.Content>
      </Card>
    </InboxListStyled>
  )
}

InboxList.displayName = 'InboxList'
